import type { FC } from 'react';
import { useEffect, useRef } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useLazyQueryPromiseInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryPromiseInterop.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	type ContentUnifiedQueryType,
	type ContentBlogUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentBlogUnifiedQueryVariablesType,
} from '@confluence/content-unified-query';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useHistory } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useContentType } from '@confluence/page-context';

import {
	patchTaskStatusInContentCache,
	type TaskStatusPatch,
} from './patchTaskStatusInContentCache.ts';

export type ContentRefetchHandlerProps = {
	contentId?: string;
	shouldRefetch: boolean;
	refetchImmediately?: boolean;
	onRefetchComplete?: (versionNumber?: number | null) => void;
	flushTaskStatusPatch?: () => TaskStatusPatch;
};

type ChooseContentUnifiedQueryType = ContentUnifiedQueryType | ContentBlogUnifiedQueryType;
type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

export const ContentRefetchHandler: FC<ContentRefetchHandlerProps> = ({
	contentId,
	shouldRefetch,
	refetchImmediately,
	onRefetchComplete,
	flushTaskStatusPatch,
}) => {
	const history = useHistory();
	const [contentType] = useContentType();
	const spaceKey = useSpaceKey();
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR || window.__SSR_RENDERED__);
	const chooseContentUnifiedQuery =
		contentType === 'blogpost' ? ContentBlogUnifiedQueryArtifact : ContentUnifiedQueryArtifact;

	const executeQuery = useLazyQueryPromiseInterop<
		ChooseContentUnifiedQueryType,
		ChooseContentUnifiedQueryVariablesType
	>(chooseContentUnifiedQuery, { fetchPolicy: 'network-only' });

	// Store onRefetchComplete in a ref so the effect can always call the latest
	// version without needing it in the dependency array. This prevents callers
	// that pass an unstable function reference from causing an infinite refetch
	// loop when refetchImmediately is true.
	const onRefetchCompleteRef = useRef(onRefetchComplete);
	onRefetchCompleteRef.current = onRefetchComplete;

	const flushTaskStatusPatchRef = useRef(flushTaskStatusPatch);
	flushTaskStatusPatchRef.current = flushTaskStatusPatch;

	// Runs when the page is left (contentId changes or unmount). flushTaskStatusPatch drains the
	// patch context captured at toggle time; this component's own contentId can't be used because
	// the renderer instance is reused across SPA navigations and its contentId drifts to the next
	// page before this fires. Running on leave keeps the broadcasting cache write off the mounted
	// renderer.
	useEffect(() => {
		return () => {
			const flush = flushTaskStatusPatchRef.current;
			if (!flush) {
				return;
			}
			const { taskStatusUpdates, ...context } = flush();
			if (taskStatusUpdates.length === 0) {
				return;
			}
			patchTaskStatusInContentCache({ ...context, taskStatusUpdates });
		};
	}, [contentId]);

	useEffect(() => {
		let unlisten;
		if (shouldRefetch && contentId) {
			const queryVariable: ChooseContentUnifiedQueryVariablesType = {
				contentId,
				spaceKey,
				isSSR,
				useNewContentTopper: true,
			};

			const refetchFn = async () => {
				let versionNumber: number | null | undefined;
				try {
					const queryResult = await executeQuery({
						context: { single: true },
						variables: queryVariable,
					});
					versionNumber = queryResult.content?.nodes?.[0]?.version?.number;
				} catch {
					// NOTE: We intentionally swallow any possible error here as this query is only aimed to
					// populate the cache with just published content. Should any error occur here after
					// successful publishing the page, the Apollo cache for the query will be invalidated
					// anyway and View Page will fetch the page and handle possible errors.
				}

				// If a callback is supplied, call it regardless
				onRefetchCompleteRef.current && onRefetchCompleteRef.current(versionNumber);
			};

			if (refetchImmediately) {
				void refetchFn();
			} else {
				unlisten = history?.listen(refetchFn);
			}
		}

		return () => {
			// Call the unlistener when this component unmounts
			unlisten && unlisten();
		};
	}, [shouldRefetch, executeQuery, contentId, spaceKey, isSSR, history, refetchImmediately]);
	return null;
};
